import { Check, Clear } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import {
  AlertSeverity,
  AppRole,
  setAlert,
  useAppDispatch,
  useAppSelector,
  useGetClientRolesQuery,
  useGetRolesForUserQuery,
  useGetUserPowerBiReportsQuery,
  useUpdateClientRolesForUserMutation,
} from 'api';
import { TabPanelContainer } from 'app/common';
import { useState, useEffect } from 'react';

export const ClientRolesTabPanel: React.FC<{ value: number; index: number }> =
  ({ value, index }) => {
    const dispatch = useAppDispatch();
    const { selectedUser } = useAppSelector(state => state.userMaintenance);
    const { data: clientRoles } = useGetClientRolesQuery();
    const { data: clientUserRoles, refetch } = useGetRolesForUserQuery(
      selectedUser?.id
    );
    const { refetch: refetchReports } = useGetUserPowerBiReportsQuery(
      selectedUser?.id
    );

    const [updateAction, { isLoading: isUpdating, isSuccess, isError }] =
      useUpdateClientRolesForUserMutation();
    const [clientUserRolesState, setClientUserRolesState] =
      useState<AppRole[]>();

    useEffect(() => {
      if (clientUserRoles && clientRoles && clientRoles.length > 0) {
        const updateClientRoles = clientRoles
          .map(cr => {
            const foundRole = clientUserRoles.find(cur => cr.id === cur.id);
            if (foundRole) return { ...foundRole, active: true };
            return { ...cr, active: false };
          })
          .sort((a, b) => a.name.localeCompare(b.name));

        setClientUserRolesState(updateClientRoles);
      }
      // eslint-disable-next-line
    }, [clientRoles, clientUserRoles]);

    function handleCheckboxChange(
      e: React.ChangeEvent<HTMLInputElement>,
      role: AppRole
    ) {
      const updatedUserRoles = clientUserRolesState?.map(ur =>
        ur.id === role.id ? { ...ur, active: !ur.active } : { ...ur }
      );
      setClientUserRolesState(updatedUserRoles);
    }

    async function handleSave() {
      const roleIds: number[] | undefined = clientUserRolesState
        ?.map(ur => (ur.active ? ur.id : undefined))
        .filter((x): x is number => x !== undefined);

      if (roleIds && selectedUser) {
        await updateAction({ uid: selectedUser?.id, roleIds })
          .unwrap()
          .then(() => {
            dispatch(
              setAlert({
                message: 'Roles saved.',
                severity: AlertSeverity.Success,
              })
            );
            refetch();
            refetchReports();
          })
          .catch(() => {
            dispatch(
              setAlert({
                message: 'Something went wrong.',
                severity: AlertSeverity.Error,
              })
            );
          });
      }
    }

    const getStartIcon = () => {
      if (isUpdating)
        return <CircularProgress size={20} sx={{ color: grey[50] }} />;
      else if (isSuccess) return <Check sx={{ color: grey[50] }} />;
      else if (isError) return <Clear sx={{ color: grey[50] }} />;
    };

    return (
      <TabPanelContainer value={value} index={index}>
        <Box sx={{ columnCount: 5, minWidth: '800px' }}>
          <FormGroup>
            {clientUserRolesState &&
              selectedUser &&
              clientUserRolesState.map((role, index) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={role.active}
                      onChange={e => handleCheckboxChange(e, role)}
                    />
                  }
                  key={`${role.id}-${index}`}
                  label={role.name}
                />
              ))}
          </FormGroup>
        </Box>

        <Box display="flex" justifyContent="center" paddingTop="5px">
          {clientUserRolesState && selectedUser && (
            <Button
              variant="contained"
              size="large"
              sx={{ width: '300px', display: 'flex', alignItems: 'center' }}
              onClick={handleSave}
              startIcon={getStartIcon()}
            >
              <Typography>Save</Typography>
            </Button>
          )}
        </Box>
      </TabPanelContainer>
    );
  };
